/**
 * Business Domains Settings Page
 *
 * Manage business domains (top-level organizational groupings).
 * Similar to categories but higher level - e.g., Permits, Bookkeeping, Fleet, HR
 * Domains can have categories linked to them (many-to-many relationship).
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import {
  Typography,
  Input,
  ColorPicker,
  Popconfirm,
  Spin,
  Empty,
  Tooltip,
  Switch,
  Button,
  Space,
  Drawer,
  Checkbox,
  Divider,
  message,
} from 'antd';
import {
  DeleteOutlined,
  PlusOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  ExclamationCircleOutlined,
  AppstoreOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { businessDomainsApi, categoriesApi } from '@api/index';
import type { BusinessDomainWithCounts, Category } from '@/types';
import { useAutoSave } from '@hooks/useAutoSave';

const { Title, Text } = Typography;

interface EditableDomain extends BusinessDomainWithCounts {
  isNew?: boolean;
}

// Individual domain row with inline editing
function DomainRow({
  domain,
  onUpdate,
  onDelete,
  onCreated,
  onManageCategories,
}: {
  domain: EditableDomain;
  onUpdate: (id: string, data: Partial<EditableDomain>) => Promise<void>;
  onDelete: (id: string) => void;
  onCreated?: (domain: BusinessDomainWithCounts) => void;
  onManageCategories: (domain: EditableDomain) => void;
}) {
  const [name, setName] = useState(domain.name);
  const [description, setDescription] = useState(domain.description || '');
  const [color, setColor] = useState(domain.color || '#6366f1');
  const [isActive, setIsActive] = useState(domain.isActive);
  const [isVisible, setIsVisible] = useState(domain.isVisible);
  const [isCreating, setIsCreating] = useState(false);
  const inputRef = useRef<any>(null);
  const queryClient = useQueryClient();

  // Auto-save for updates
  const { save, status } = useAutoSave({
    saveFn: async (data: Partial<EditableDomain>) => {
      await onUpdate(domain.id, data);
    },
    queryKey: 'business-domains',
    debounceMs: 600,
  });

  // Create mutation for new domains
  const createMutation = useMutation({
    mutationFn: async (data: { name: string; color: string; description?: string }) => {
      return businessDomainsApi.create({
        name: data.name,
        description: data.description,
        color: data.color,
        isActive: true,
        isVisible: true,
      });
    },
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['business-domains'] });
      onCreated?.(created as BusinessDomainWithCounts);
      setName('');
      setDescription('');
      setColor('#6366f1');
      setIsCreating(false);
    },
    onError: () => {
      setIsCreating(false);
    },
  });

  // Focus input for new rows
  useEffect(() => {
    if (domain.isNew && inputRef.current) {
      inputRef.current.focus();
    }
  }, [domain.isNew]);

  const handleNameChange = useCallback(
    (value: string) => {
      setName(value);
      if (!domain.isNew && value.trim()) {
        save({ name: value });
      }
    },
    [domain.isNew, save]
  );

  const handleDescriptionChange = useCallback(
    (value: string) => {
      setDescription(value);
      if (!domain.isNew) {
        save({ description: value });
      }
    },
    [domain.isNew, save]
  );

  const handleColorChange = useCallback(
    (newColor: any) => {
      const hexColor = typeof newColor === 'string' ? newColor : newColor?.toHexString?.() || '#6366f1';
      setColor(hexColor);
      if (!domain.isNew) {
        save({ color: hexColor });
      }
    },
    [domain.isNew, save]
  );

  const handleActiveChange = useCallback(
    (checked: boolean) => {
      setIsActive(checked);
      if (!domain.isNew) {
        save({ isActive: checked });
      }
    },
    [domain.isNew, save]
  );

  const handleVisibleChange = useCallback(
    (checked: boolean) => {
      setIsVisible(checked);
      if (!domain.isNew) {
        save({ isVisible: checked });
      }
    },
    [domain.isNew, save]
  );

  const handleNameBlur = useCallback(() => {
    if (domain.isNew && name.trim() && !isCreating) {
      setIsCreating(true);
      createMutation.mutate({ name: name.trim(), color, description: description.trim() || undefined });
    }
  }, [domain.isNew, name, color, description, isCreating, createMutation]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && domain.isNew && name.trim() && !isCreating) {
        setIsCreating(true);
        createMutation.mutate({ name: name.trim(), color, description: description.trim() || undefined });
      }
    },
    [domain.isNew, name, color, description, isCreating, createMutation]
  );

  // Status indicator
  const StatusIcon = () => {
    if (isCreating || createMutation.isPending) {
      return <SyncOutlined spin style={{ color: '#1890ff', fontSize: 12 }} />;
    }
    switch (status) {
      case 'pending':
      case 'saving':
        return <SyncOutlined spin style={{ color: '#1890ff', fontSize: 12 }} />;
      case 'saved':
        return <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 12 }} />;
      case 'error':
        return <ExclamationCircleOutlined style={{ color: '#ff4d4f', fontSize: 12 }} />;
      default:
        return null;
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 0',
        borderBottom: '1px solid #f0f0f0',
      }}
    >
      {/* Color dot with picker */}
      <ColorPicker
        value={color}
        onChange={handleColorChange}
        size="small"
        presets={[
          {
            label: 'Colors',
            colors: [
              '#ef4444', '#f97316', '#faad14', '#22c55e', '#3b82f6',
              '#a855f7', '#ec4899', '#6366f1', '#14b8a6', '#6b7280',
            ],
          },
        ]}
      >
        <div
          style={{
            width: 24,
            height: 24,
            borderRadius: 6,
            backgroundColor: color,
            cursor: 'pointer',
            border: '2px solid #fff',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            flexShrink: 0,
          }}
        />
      </ColorPicker>

      {/* Name input */}
      <Input
        ref={inputRef}
        value={name}
        onChange={(e) => handleNameChange(e.target.value)}
        onBlur={handleNameBlur}
        onKeyDown={handleKeyDown}
        placeholder={domain.isNew ? 'New domain name...' : 'Domain name'}
        variant="borderless"
        style={{ flex: 1, maxWidth: 160, fontWeight: 600 }}
      />

      {/* Description input */}
      <Input
        value={description}
        onChange={(e) => handleDescriptionChange(e.target.value)}
        placeholder="Description (optional)"
        variant="borderless"
        style={{ flex: 2, color: '#666' }}
      />

      {/* Category count with manage button */}
      {!domain.isNew && (
        <Button
          type="text"
          size="small"
          icon={<AppstoreOutlined />}
          onClick={() => onManageCategories(domain)}
          style={{ color: '#666' }}
        >
          {domain.categoryCount || 0} categories
        </Button>
      )}

      {/* Template count */}
      {!domain.isNew && domain.templateCount !== undefined && (
        <Text type="secondary" style={{ minWidth: 70, textAlign: 'center' }}>
          {domain.templateCount} templates
        </Text>
      )}

      {/* Visible toggle */}
      {!domain.isNew && (
        <Tooltip title={isVisible ? 'Visible' : 'Hidden'}>
          <Switch
            size="small"
            checked={isVisible}
            onChange={handleVisibleChange}
            checkedChildren="V"
            unCheckedChildren="H"
          />
        </Tooltip>
      )}

      {/* Active toggle */}
      {!domain.isNew && (
        <Tooltip title={isActive ? 'Active' : 'Inactive'}>
          <Switch
            size="small"
            checked={isActive}
            onChange={handleActiveChange}
          />
        </Tooltip>
      )}

      {/* Status indicator */}
      <div style={{ width: 20, display: 'flex', justifyContent: 'center' }}>
        <StatusIcon />
      </div>

      {/* Delete button */}
      {!domain.isNew && (
        <Popconfirm
          title="Delete business domain?"
          description={
            (domain.templateCount ?? 0) > 0
              ? `This domain has ${domain.templateCount} templates. They will need to be reassigned.`
              : 'This action cannot be undone.'
          }
          onConfirm={() => onDelete(domain.id)}
          okText="Delete"
          okButtonProps={{ danger: true }}
        >
          <DeleteOutlined
            style={{
              color: '#999',
              cursor: 'pointer',
              padding: 4,
            }}
          />
        </Popconfirm>
      )}

      {/* Add icon for new row */}
      {domain.isNew && (
        <PlusOutlined style={{ color: '#999', padding: 4 }} />
      )}
    </div>
  );
}

// Category linking drawer
function CategoryLinkDrawer({
  domain,
  open,
  onClose,
}: {
  domain: EditableDomain | null;
  open: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [linkedCategoryIds, setLinkedCategoryIds] = useState<Set<string>>(new Set());

  // Fetch all categories
  const { data: categories = [], isLoading } = useQuery({
    queryKey: ['categories'],
    queryFn: () => categoriesApi.list(),
    enabled: open,
  });

  // Initialize linked categories when domain changes
  useEffect(() => {
    if (domain?.categories) {
      setLinkedCategoryIds(new Set(domain.categories.map(c => c.id)));
    } else {
      setLinkedCategoryIds(new Set());
    }
  }, [domain]);

  // Link category mutation
  const linkMutation = useMutation({
    mutationFn: async (categoryIds: string[]) => {
      if (!domain) return;
      return businessDomainsApi.linkCategories(domain.id, categoryIds);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['business-domains'] });
      message.success('Category linked');
    },
    onError: () => {
      message.error('Failed to link category');
    },
  });

  // Unlink category mutation
  const unlinkMutation = useMutation({
    mutationFn: async (categoryId: string) => {
      if (!domain) return;
      return businessDomainsApi.unlinkCategory(domain.id, categoryId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['business-domains'] });
      message.success('Category unlinked');
    },
    onError: () => {
      message.error('Failed to unlink category');
    },
  });

  const handleToggleCategory = (categoryId: string, checked: boolean) => {
    if (!domain) return;

    if (checked) {
      setLinkedCategoryIds(prev => new Set([...prev, categoryId]));
      linkMutation.mutate([categoryId]);
    } else {
      setLinkedCategoryIds(prev => {
        const next = new Set(prev);
        next.delete(categoryId);
        return next;
      });
      unlinkMutation.mutate(categoryId);
    }
  };

  return (
    <Drawer
      title={
        <Space>
          <div
            style={{
              width: 20,
              height: 20,
              borderRadius: 4,
              backgroundColor: domain?.color || '#6366f1',
            }}
          />
          <span>Link Categories to {domain?.name}</span>
        </Space>
      }
      placement="right"
      width={400}
      open={open}
      onClose={onClose}
    >
      {isLoading ? (
        <Spin />
      ) : categories.length === 0 ? (
        <Empty description="No categories available" />
      ) : (
        <div>
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            Select categories that belong to this business domain. Categories can belong to multiple domains.
          </Text>
          <Divider style={{ margin: '16px 0' }} />
          {categories.map((category: Category) => (
            <div
              key={category.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '8px 0',
                borderBottom: '1px solid #f5f5f5',
              }}
            >
              <Checkbox
                checked={linkedCategoryIds.has(category.id)}
                onChange={(e) => handleToggleCategory(category.id, e.target.checked)}
              />
              <div
                style={{
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  backgroundColor: category.color || '#1890ff',
                  flexShrink: 0,
                }}
              />
              <div style={{ flex: 1 }}>
                <Text strong>{category.name}</Text>
                {category.description && (
                  <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                    {category.description}
                  </Text>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </Drawer>
  );
}

export default function BusinessDomainsSettingsPage() {
  const queryClient = useQueryClient();
  const [categoryDrawerDomain, setCategoryDrawerDomain] = useState<EditableDomain | null>(null);

  // Fetch domains with counts
  const { data: domains = [], isLoading } = useQuery({
    queryKey: ['business-domains', 'with-categories'],
    queryFn: () => businessDomainsApi.listWithCategories(),
  });

  // Update mutation
  const updateDomain = useCallback(
    async (id: string, data: Partial<EditableDomain>) => {
      const apiData: Parameters<typeof businessDomainsApi.update>[1] = {};
      if (data.name !== undefined) apiData.name = data.name;
      if (data.description !== undefined) apiData.description = data.description || undefined;
      if (data.color !== undefined) apiData.color = data.color;
      if (data.icon !== undefined) apiData.icon = data.icon || undefined;
      if (data.sortOrder !== undefined) apiData.sortOrder = data.sortOrder;
      if (data.isActive !== undefined) apiData.isActive = data.isActive;
      if (data.isVisible !== undefined) apiData.isVisible = data.isVisible;

      await businessDomainsApi.update(id, apiData);
      queryClient.invalidateQueries({ queryKey: ['business-domains'] });
    },
    [queryClient]
  );

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => businessDomainsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['business-domains'] });
      message.success('Business domain deleted');
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.error || 'Failed to delete domain');
    },
  });

  const handleDelete = useCallback(
    (id: string) => {
      deleteMutation.mutate(id);
    },
    [deleteMutation]
  );

  const handleManageCategories = useCallback((domain: EditableDomain) => {
    setCategoryDrawerDomain(domain);
  }, []);

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <Spin />
      </div>
    );
  }

  // Create a "new domain" placeholder row
  const newDomainPlaceholder: EditableDomain = {
    id: 'new',
    tenantId: '',
    name: '',
    slug: '',
    description: '',
    color: '#6366f1',
    icon: '',
    isVisible: true,
    sortOrder: domains.length,
    isActive: true,
    createdAt: '',
    updatedAt: '',
    categoryCount: 0,
    templateCount: 0,
    isNew: true,
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={4} style={{ margin: 0, marginBottom: 4 }}>
          Business Domains
        </Title>
        <Text type="secondary">
          Organize your work into high-level business areas like Permits, Bookkeeping, Fleet, HR, etc.
          Categories can be linked to multiple domains.
        </Text>
      </div>

      {/* Header row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '8px 0',
          borderBottom: '2px solid #f0f0f0',
          color: '#888',
          fontSize: 12,
          fontWeight: 500,
          textTransform: 'uppercase',
        }}
      >
        <div style={{ width: 24 }}>Color</div>
        <div style={{ flex: 1, maxWidth: 160 }}>Name</div>
        <div style={{ flex: 2 }}>Description</div>
        <div style={{ minWidth: 120 }}>Categories</div>
        <div style={{ minWidth: 70, textAlign: 'center' }}>Templates</div>
        <div style={{ width: 48, textAlign: 'center' }}>Show</div>
        <div style={{ width: 40, textAlign: 'center' }}>Active</div>
        <div style={{ width: 20 }}></div>
        <div style={{ width: 24 }}></div>
      </div>

      {/* Domain rows */}
      {domains.length === 0 && (
        <Empty
          description="No business domains yet"
          style={{ padding: '40px 0' }}
        />
      )}

      {domains.map((domain) => (
        <DomainRow
          key={domain.id}
          domain={domain}
          onUpdate={updateDomain}
          onDelete={handleDelete}
          onManageCategories={handleManageCategories}
        />
      ))}

      {/* New domain row */}
      <DomainRow
        key="new"
        domain={newDomainPlaceholder}
        onUpdate={updateDomain}
        onDelete={handleDelete}
        onManageCategories={handleManageCategories}
        onCreated={() => {
          queryClient.invalidateQueries({ queryKey: ['business-domains'] });
        }}
      />

      <div style={{ marginTop: 16 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Tip: Click the color block to change domain color. Click "categories" to link or unlink categories from a domain.
        </Text>
      </div>

      {/* Category linking drawer */}
      <CategoryLinkDrawer
        domain={categoryDrawerDomain}
        open={!!categoryDrawerDomain}
        onClose={() => setCategoryDrawerDomain(null)}
      />
    </div>
  );
}
